// SCR-STAFF-304 — برنامه کاری (US-SCH-001، 003، 004): الگوی هفتگی هر ارائه‌دهنده در شعبه فعال (شبکه روز × ساعت با بازه‌ها و اتاق
// پیش‌فرض، ذخیره پیش‌نویس و انتشار)، استثناها و مرخصی با پیش‌نمایش اثر روی نوبت‌ها و تأیید (AC-SCH-003.1)، و افق رزرو
// (sch.booking_horizon_days در Configuration؛ ویرایش با sch.policy.manage).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AppointmentChannel,
  type ExceptionImpactView,
  type NamedOption,
  type SchBlockTimeType,
  type ScheduleExceptionRequest,
  type ScheduleExceptionType,
  type ScheduleExceptionView,
  type WorkBlockRequest,
  type WorkScheduleView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, ErrorState, faDigits, LoadingState, PageHeader, latinDigits, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { blockColor, BranchRequired, IRANIAN_WEEK, schKeys, useActiveBranch, useBookingOptions } from './common';

export const SCR_STAFF_304 = 'SCR-STAFF-304';

const HORIZON_KEY = 'sch.booking_horizon_days';
const GRID_FROM = 7 * 60;
const GRID_TO = 22 * 60;
const EXCEPTION_TYPES: readonly ScheduleExceptionType[] = ['Leave', 'PartialLeave', 'ExtraHours', 'RoomChange'];

interface BlockDraft {
  key: string;
  weekday: number;
  start: string;
  end: string;
  roomId: string;
  /** دسته‌های خدمت مجاز بلوک (BR-SCH-003؛ خالی = همه). */
  categoryIds: string[];
  /** کانال‌های مجاز بلوک (خالی = همه). */
  channels: string[];
  /** نوع زمان (AC-SCH-130.1): بلوک `Referral` فقط به سیب منتشر می‌شود و زمان عادی در آن رزرو نمی‌شود. */
  timeType: SchBlockTimeType;
  /** رنگ تقویم `#RRGGBB` (خالی = رنگ پیش‌فرض). */
  calendarColor: string;
  /** محل خدمت بلوک ارجاعی — هر سه بخش برای `Referral` الزامی است (PHX-SCH-0331). */
  serviceBuilding: string;
  serviceFloor: string;
  serviceBlock: string;
  /** محل پذیرش بلوک ارجاعی — ساختمان و طبقه برای `Referral` الزامی است. */
  receptionBuilding: string;
  receptionFloor: string;
}

/** نوع‌های زمان بلوک (AC-SCH-130.1). */
const BLOCK_TIME_TYPES: readonly SchBlockTimeType[] = ['Regular', 'Referral'];

/** بلوک تازه: زمان عادی بی رنگ و بی محل، تا کاربر «ارجاعی» را آگاهانه انتخاب کند. */
function emptyBlock(weekday: number, roomId: string): BlockDraft {
  return {
    key: draftKey(),
    weekday,
    start: '09:00',
    end: '17:00',
    roomId,
    categoryIds: [],
    channels: [],
    timeType: 'Regular',
    calendarColor: '',
    serviceBuilding: '',
    serviceFloor: '',
    serviceBlock: '',
    receptionBuilding: '',
    receptionFloor: '',
  };
}

/** کانال‌های قابل محدودسازی بلوک (US-SCH-002). */
const BLOCK_CHANNELS = ['InPerson', 'Phone', 'Portal', 'Widget'] as const;

let draftSequence = 0;
function draftKey(): string {
  draftSequence += 1;
  return `b${draftSequence}`;
}

function minutes(value: string): number {
  const [hour, minute] = latinDigits(value).split(':').map(Number) as [number, number];
  return hour * 60 + minute;
}

function hhmm(value: string): string {
  return value.slice(0, 5);
}

function toDrafts(template: WorkScheduleView | undefined): BlockDraft[] {
  return (template?.blocks ?? []).map((block) => ({
    key: draftKey(),
    weekday: Number(block.weekday),
    start: hhmm(block.start),
    end: hhmm(block.end),
    roomId: block.defaultRoomId ?? '',
    categoryIds: [...(block.serviceCategoryIds ?? [])],
    channels: [...(block.channels ?? [])],
    timeType: block.timeType ?? 'Regular',
    calendarColor: block.calendarColor ?? '',
    serviceBuilding: block.serviceLocation?.building ?? '',
    serviceFloor: block.serviceLocation?.floor ?? '',
    serviceBlock: block.serviceLocation?.block ?? '',
    receptionBuilding: block.receptionLocation?.building ?? '',
    receptionFloor: block.receptionLocation?.floor ?? '',
  }));
}

/** نوار ساعت روز (شبکه روز × ساعت) برای بازه‌های یک روز. */
function DayBar({ blocks, label }: { blocks: readonly BlockDraft[]; label: string }) {
  const width = GRID_TO - GRID_FROM;
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{ position: 'relative', height: 14, bgcolor: 'action.hover', borderRadius: 1, minWidth: 160, flex: 1 }}
    >
      {blocks
        .filter((block) => block.start && block.end && minutes(block.end) > minutes(block.start))
        .map((block) => {
          const from = Math.max(minutes(block.start), GRID_FROM) - GRID_FROM;
          const to = Math.min(minutes(block.end), GRID_TO) - GRID_FROM;
          const color = blockColor(block.timeType, block.calendarColor);
          return (
            <Box
              key={block.key}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                insetInlineStart: `${(from / width) * 100}%`,
                width: `${(Math.max(to - from, 0) / width) * 100}%`,
                bgcolor: color ?? 'primary.main',
                borderRadius: 1,
              }}
            />
          );
        })}
    </Box>
  );
}

function TemplateEditor({
  providerId,
  branchId,
  rooms,
  categories,
  canManage,
  today,
}: {
  providerId: string;
  branchId: string;
  rooms: readonly NamedOption[];
  categories: readonly NamedOption[];
  canManage: boolean;
  today: string;
}) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [drafts, setDrafts] = useState<BlockDraft[]>([]);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: schKeys.schedules(session?.tenantId, providerId, branchId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/scheduling/work-schedules', { params: { query: { providerId, branchId } } })),
  });
  const template = useMemo(
    () => [...(templates.data?.items ?? [])].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0],
    [templates.data],
  );

  useEffect(() => {
    setDrafts(toDrafts(template));
    setValidFrom(formatJalaliInput(template?.validFrom ?? today));
    setValidTo(formatJalaliInput(template?.validTo ?? null));
  }, [template, today]);

  const blocks = (): WorkBlockRequest[] =>
    drafts.map((draft) => ({
      weekday: draft.weekday,
      start: `${draft.start}:00`,
      end: `${draft.end}:00`,
      defaultRoomId: draft.roomId || null,
      serviceCategoryIds: draft.categoryIds,
      channels: draft.channels as AppointmentChannel[],
      timeType: draft.timeType,
      calendarColor: draft.calendarColor.trim() || null,
      // AC-SCH-130.1: محل خدمت و پذیرش فقط برای بلوک ارجاعی معنا دارد؛ دامنه در بلوک عادی آن را دور می‌ریزد.
      serviceLocation:
        draft.timeType === 'Referral'
          ? {
              building: draft.serviceBuilding.trim(),
              floor: draft.serviceFloor.trim(),
              block: draft.serviceBlock.trim(),
              roomId: draft.roomId || null,
            }
          : null,
      receptionLocation:
        draft.timeType === 'Referral'
          ? { building: draft.receptionBuilding.trim(), floor: draft.receptionFloor.trim() }
          : null,
    }));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });

  const save = useMutation({
    mutationFn: async (): Promise<WorkScheduleView> => {
      const from = parseJalaliInput(validFrom) ?? null;
      const to = validTo.trim() ? (parseJalaliInput(validTo) ?? 'invalid') : null;
      if (template) {
        return unwrap(
          tenantApi.PUT('/api/v1/scheduling/work-schedules/{id}', {
            params: { path: { id: template.id } },
            body: { validFrom: from, validTo: to, blocks: blocks(), rowVersion: Number(template.rowVersion) },
          }),
        );
      }
      return unwrap(
        tenantApi.POST('/api/v1/scheduling/work-schedules', {
          body: { providerId, branchId, validFrom: from, validTo: to, blocks: blocks() },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: async () => {
      await invalidate();
      setMessage(t('sch:schedules.saved'));
    },
  });

  const publish = useMutation({
    mutationFn: async (): Promise<WorkScheduleView> => {
      const saved = await save.mutateAsync();
      return unwrap(
        tenantApi.POST('/api/v1/scheduling/work-schedules/{id}:publish', { params: { path: { id: saved.id } } }),
      );
    },
    onSuccess: async () => {
      await invalidate();
      setMessage(t('sch:schedules.published'));
    },
  });

  if (templates.isPending) {
    return <LoadingState rows={4} />;
  }
  if (templates.error) {
    return <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />;
  }

  const update = (key: string, changes: Partial<BlockDraft>) =>
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...changes } : draft)));

  return (
    <Card variant="outlined" data-testid="schedule-template">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="h2">
            {t('sch:schedules.template')}
          </Typography>
          {template && (
            <Chip
              size="small"
              color={template.status === 'Published' ? 'success' : 'default'}
              label={t(`sch:schedules.status_${template.status}`)}
              data-testid="schedule-status"
            />
          )}
          {template?.hasUnpublishedChanges && <Chip size="small" color="warning" label={t('sch:schedules.unpublished_changes')} />}
        </Stack>
        {!template && <Alert severity="info" sx={{ mb: 2 }}>{t('sch:schedules.no_template')}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('sch:schedules.valid_from')}
            value={validFrom}
            onChange={(event) => setValidFrom(event.target.value)}
            disabled={!canManage}
            placeholder={t('sch:schedules.date_placeholder')}
            inputProps={{ dir: 'ltr', 'data-testid': 'schedule-valid-from' }}
          />
          <TextField
            label={t('sch:schedules.valid_to')}
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
            disabled={!canManage}
            placeholder={t('sch:schedules.date_placeholder')}
            inputProps={{ dir: 'ltr' }}
          />
        </Stack>
        <Table size="small" aria-label={t('sch:schedules.grid_label')} data-testid="schedule-grid">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 110 }}>{t('sch:schedules.weekday')}</TableCell>
              <TableCell>{t('sch:schedules.grid_hours')}</TableCell>
              <TableCell sx={{ width: 140 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {IRANIAN_WEEK.map((weekday) => {
              const dayName = t(`sch:weekday.${weekday}`);
              const dayBlocks = drafts.filter((draft) => draft.weekday === weekday);
              return (
                <TableRow key={weekday} data-testid={`schedule-day-${weekday}`}>
                  <TableCell>{dayName}</TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <DayBar
                        blocks={dayBlocks}
                        label={
                          dayBlocks.length === 0
                            ? `${dayName}: ${t('sch:schedules.day_off')}`
                            : `${dayName}: ${dayBlocks
                                .map((block) => t('sch:schedules.hours_range', { start: faDigits(block.start), end: faDigits(block.end) }))
                                .join(t('sch:schedules.list_separator'))}`
                        }
                      />
                      {dayBlocks.map((block) => (
                        <Stack key={block.key} spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            type="time"
                            label={t('sch:schedules.start')}
                            value={block.start}
                            onChange={(event) => update(block.key, { start: event.target.value })}
                            disabled={!canManage}
                            inputProps={{ step: 300, 'data-testid': 'block-start' }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            size="small"
                            type="time"
                            label={t('sch:schedules.end')}
                            value={block.end}
                            onChange={(event) => update(block.key, { end: event.target.value })}
                            disabled={!canManage}
                            inputProps={{ step: 300, 'data-testid': 'block-end' }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            select
                            size="small"
                            label={t('sch:schedules.default_room')}
                            value={block.roomId}
                            onChange={(event) => update(block.key, { roomId: event.target.value })}
                            disabled={!canManage}
                            sx={{ minWidth: 160 }}
                            SelectProps={{ displayEmpty: true }}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value="">{t('sch:schedules.no_room')}</MenuItem>
                            {rooms.map((room) => (
                              <MenuItem key={room.id} value={room.id}>
                                {room.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label={t('sch:schedules.block_categories')}
                            value={block.categoryIds}
                            onChange={(event) => {
                              const value = event.target.value as unknown as string[] | string;
                              update(block.key, { categoryIds: typeof value === 'string' ? value.split(',') : value });
                            }}
                            disabled={!canManage}
                            sx={{ minWidth: 180 }}
                            SelectProps={{
                              multiple: true,
                              displayEmpty: true,
                              renderValue: (selected) =>
                                (selected as string[]).length === 0
                                  ? t('sch:schedules.all_categories')
                                  : (selected as string[]).map((id) => categories.find((item) => item.id === id)?.name ?? '').join(t('sch:schedules.list_separator')),
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'block-categories' }}
                          >
                            {categories.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label={t('sch:schedules.block_channels')}
                            value={block.channels}
                            onChange={(event) => {
                              const value = event.target.value as unknown as string[] | string;
                              update(block.key, { channels: typeof value === 'string' ? value.split(',') : value });
                            }}
                            disabled={!canManage}
                            sx={{ minWidth: 160 }}
                            SelectProps={{
                              multiple: true,
                              displayEmpty: true,
                              renderValue: (selected) =>
                                (selected as string[]).length === 0
                                  ? t('sch:schedules.all_channels')
                                  : (selected as string[]).map((channel) => t(`sch:channel.${channel}`)).join(t('sch:schedules.list_separator')),
                            }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'block-channels' }}
                          >
                            {BLOCK_CHANNELS.map((channel) => (
                              <MenuItem key={channel} value={channel}>
                                {t(`sch:channel.${channel}`)}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label={t('sch:work_block.time_type')}
                            value={block.timeType}
                            onChange={(event) =>
                              update(block.key, { timeType: event.target.value as SchBlockTimeType })
                            }
                            disabled={!canManage}
                            sx={{ minWidth: 130 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'block-time-type' }}
                          >
                            {BLOCK_TIME_TYPES.map((timeType) => (
                              <MenuItem key={timeType} value={timeType}>
                                {t(`sch:work_block.time_type_${timeType.toLowerCase()}`)}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size="small"
                            type="color"
                            label={t('sch:work_block.calendar_color')}
                            value={block.calendarColor || blockColor(block.timeType, null) || '#1976d2'}
                            onChange={(event) => update(block.key, { calendarColor: event.target.value })}
                            disabled={!canManage}
                            sx={{ minWidth: 110 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'block-calendar-color' }}
                          />
                          {canManage && (
                            <Tooltip title={t('sch:schedules.remove_block')}>
                              <IconButton
                                aria-label={t('sch:schedules.remove_block')}
                                onClick={() => setDrafts((current) => current.filter((draft) => draft.key !== block.key))}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                        {/* AC-SCH-130.1: محل خدمت و محل پذیرش بلوک ارجاعی — سیب بی آن‌ها ظرفیت را نمی‌پذیرد. */}
                        {block.timeType === 'Referral' && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ flexWrap: 'wrap', pl: 1, borderInlineStart: 2, borderColor: 'divider' }}
                            data-testid={`block-referral-${block.key}`}
                          >
                            <TextField
                              size="small"
                              label={t('sch:work_block.service_building')}
                              value={block.serviceBuilding}
                              onChange={(event) => update(block.key, { serviceBuilding: event.target.value })}
                              disabled={!canManage}
                              required
                              sx={{ minWidth: 140 }}
                              inputProps={{ maxLength: 100, 'data-testid': 'block-service-building' }}
                            />
                            <TextField
                              size="small"
                              label={t('sch:work_block.service_floor')}
                              value={block.serviceFloor}
                              onChange={(event) => update(block.key, { serviceFloor: event.target.value })}
                              disabled={!canManage}
                              required
                              sx={{ minWidth: 120 }}
                              inputProps={{ maxLength: 100, 'data-testid': 'block-service-floor' }}
                            />
                            <TextField
                              size="small"
                              label={t('sch:work_block.service_block')}
                              value={block.serviceBlock}
                              onChange={(event) => update(block.key, { serviceBlock: event.target.value })}
                              disabled={!canManage}
                              required
                              sx={{ minWidth: 120 }}
                              inputProps={{ maxLength: 100, 'data-testid': 'block-service-block' }}
                            />
                            <TextField
                              size="small"
                              label={t('sch:work_block.reception_building')}
                              value={block.receptionBuilding}
                              onChange={(event) => update(block.key, { receptionBuilding: event.target.value })}
                              disabled={!canManage}
                              required
                              sx={{ minWidth: 140 }}
                              inputProps={{ maxLength: 100, 'data-testid': 'block-reception-building' }}
                            />
                            <TextField
                              size="small"
                              label={t('sch:work_block.reception_floor')}
                              value={block.receptionFloor}
                              onChange={(event) => update(block.key, { receptionFloor: event.target.value })}
                              disabled={!canManage}
                              required
                              sx={{ minWidth: 120 }}
                              inputProps={{ maxLength: 100, 'data-testid': 'block-reception-floor' }}
                              helperText={t('sch:work_block.referral_hint')}
                            />
                          </Stack>
                        )}
                        </Stack>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {canManage && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          setDrafts((current) => [
                            ...current,
                            emptyBlock(weekday, rooms[0]?.id ?? ''),
                          ])
                        }
                        data-testid={`add-block-${weekday}`}
                      >
                        {t('sch:schedules.add_block')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {(save.error ?? publish.error) ? <ErrorState error={save.error ?? publish.error} compact /> : null}
        {message && (
          <Alert severity="success" role="status" sx={{ mt: 2 }} data-testid="schedule-message">
            {message}
          </Alert>
        )}
        {canManage && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => save.mutate()} disabled={save.isPending || publish.isPending} data-testid="schedule-save">
              {t('sch:schedules.save')}
            </Button>
            <Button
              variant="contained"
              onClick={() => publish.mutate()}
              disabled={save.isPending || publish.isPending || drafts.length === 0}
              data-testid="schedule-publish"
            >
              {t('sch:schedules.publish')}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ExceptionsCard({
  providerId,
  branchId,
  rooms,
  canManage,
  today,
}: {
  providerId: string;
  branchId: string;
  rooms: readonly NamedOption[];
  canManage: boolean;
  today: string;
}) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [type, setType] = useState<ScheduleExceptionType>('Leave');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [roomId, setRoomId] = useState('');
  const [reason, setReason] = useState('');
  const [impact, setImpact] = useState<ExceptionImpactView | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const exceptions = useQuery({
    queryKey: schKeys.exceptions(session?.tenantId, providerId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/scheduling/schedule-exceptions', { params: { query: { providerId, from: today } } })),
  });

  const body = (confirmImpact: boolean): ScheduleExceptionRequest => ({
    providerId,
    branchId,
    date: parseJalaliInput(date) ?? null,
    type,
    start: type === 'PartialLeave' || type === 'ExtraHours' ? (start ? `${start}:00` : null) : null,
    end: type === 'PartialLeave' || type === 'ExtraHours' ? (end ? `${end}:00` : null) : null,
    roomId: type === 'RoomChange' ? roomId || null : null,
    reason: reason.trim() || null,
    confirmImpact,
  });

  const create = useMutation({
    mutationFn: async (confirmImpact: boolean): Promise<ScheduleExceptionView | null> => {
      if (!confirmImpact) {
        const preview = await unwrap(tenantApi.POST('/api/v1/scheduling/schedule-exceptions:impact', { body: body(false) }));
        if (Number(preview.count) > 0) {
          setImpact(preview);
          return null;
        }
      }
      return unwrap(
        tenantApi.POST('/api/v1/scheduling/schedule-exceptions', {
          body: body(true),
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: async (created) => {
      if (created) {
        setImpact(null);
        setReason('');
        setMessage(t('sch:schedules.exception_saved'));
        await queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });
      }
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.DELETE('/api/v1/scheduling/schedule-exceptions/{id}', { params: { path: { id } } })),
    onSuccess: async () => {
      setMessage(t('sch:schedules.exception_removed'));
      await queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });
    },
  });

  // US-SCH-005: تأیید یا رد مرخصی خود ارائه‌دهنده (در انتظار تأیید).
  const review = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      approve
        ? unwrap(tenantApi.POST('/api/v1/scheduling/schedule-exceptions/{id}:approve', { params: { path: { id } }, body: { note: null } }))
        : unwrap(tenantApi.POST('/api/v1/scheduling/schedule-exceptions/{id}:reject', { params: { path: { id } }, body: { note: null } })),
    onSuccess: async (_data, variables) => {
      setMessage(t(variables.approve ? 'sch:schedules.approved' : 'sch:schedules.rejected'));
      await queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });
    },
  });

  const timed = type === 'PartialLeave' || type === 'ExtraHours';

  return (
    <Card variant="outlined" data-testid="schedule-exceptions">
      <CardContent>
        <Typography variant="subtitle1" component="h2" gutterBottom>
          {t('sch:schedules.exceptions')}
        </Typography>
        {canManage && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'flex-start' }}>
            <TextField
              select
              size="small"
              label={t('sch:schedules.exception_type')}
              value={type}
              onChange={(event) => setType(event.target.value as ScheduleExceptionType)}
              sx={{ minWidth: 160 }}
              inputProps={{ 'data-testid': 'exception-type' }}
            >
              {EXCEPTION_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`sch:exception_type.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('sch:schedules.date')}
              value={date}
              onChange={(event) => setDate(event.target.value)}
              placeholder={t('sch:schedules.date_placeholder')}
              inputProps={{ dir: 'ltr', 'data-testid': 'exception-date' }}
            />
            {timed && (
              <>
                <TextField
                  size="small"
                  type="time"
                  label={t('sch:schedules.start')}
                  value={start}
                  onChange={(event) => setStart(event.target.value)}
                  inputProps={{ step: 300 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="time"
                  label={t('sch:schedules.end')}
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                  inputProps={{ step: 300 }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            {type === 'RoomChange' && (
              <TextField
                select
                size="small"
                label={t('sch:schedules.default_room')}
                value={roomId}
                onChange={(event) => setRoomId(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              label={t('sch:schedules.reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setMessage(null);
                create.mutate(false);
              }}
              disabled={create.isPending}
              data-testid="exception-add"
            >
              {t('sch:schedules.add_exception')}
            </Button>
          </Stack>
        )}
        {create.error && !impact ? <ErrorState error={create.error} compact /> : null}
        {remove.error ? <ErrorState error={remove.error} compact /> : null}
        {message && (
          <Alert severity="success" role="status" sx={{ mb: 1 }} data-testid="exception-message">
            {message}
          </Alert>
        )}
        {exceptions.isPending ? (
          <LoadingState rows={2} />
        ) : exceptions.error ? (
          <ErrorState error={exceptions.error} compact />
        ) : (exceptions.data?.items.length ?? 0) === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('sch:schedules.no_exceptions')}
          </Typography>
        ) : (
          <Table size="small" aria-label={t('sch:schedules.exceptions')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('sch:schedules.date')}</TableCell>
                <TableCell>{t('sch:schedules.exception_type')}</TableCell>
                <TableCell>{t('sch:schedules.grid_hours')}</TableCell>
                <TableCell>{t('sch:schedules.reason')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {exceptions.data?.items.map((item) => (
                <TableRow key={item.id} data-testid="exception-row">
                  <TableCell>{toJalali(item.date)}</TableCell>
                  <TableCell>
                    {item.type ? t(`sch:exception_type.${item.type}`) : ''}
                    {item.status === 'PendingApproval' && (
                      <Chip size="small" color="info" sx={{ mx: 1 }} label={t('sch:schedules.pending_approval')} data-testid="exception-pending" />
                    )}
                    {Number(item.affectedAppointments) > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        sx={{ mx: 1 }}
                        label={t('sch:schedules.affected', { n: faDigits(item.affectedAppointments) })}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.start && item.end
                      ? t('sch:schedules.hours_range', { start: faDigits(hhmm(item.start)), end: faDigits(hhmm(item.end)) })
                      : '—'}
                  </TableCell>
                  <TableCell>{item.reason}</TableCell>
                  <TableCell>
                    {canManage && item.status === 'PendingApproval' && (
                      <Stack direction="row" spacing={1} component="span">
                        <Button size="small" onClick={() => review.mutate({ id: item.id, approve: true })} data-testid="exception-approve">
                          {t('sch:schedules.approve')}
                        </Button>
                        <Button size="small" color="error" onClick={() => review.mutate({ id: item.id, approve: false })} data-testid="exception-reject">
                          {t('sch:schedules.reject')}
                        </Button>
                      </Stack>
                    )}
                    {canManage && item.status !== 'PendingApproval' && (
                      <Tooltip title={t('sch:schedules.remove_exception')}>
                        <IconButton aria-label={t('sch:schedules.remove_exception')} onClick={() => remove.mutate(item.id)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ConfirmDialog
        open={impact !== null}
        titleKey="sch:schedules.impact_title"
        messageKey="sch:schedules.impact_message"
        messageValues={{ n: faDigits(impact?.count ?? 0) }}
        confirmKey="sch:schedules.impact_confirm"
        destructive
        busy={create.isPending}
        error={create.error}
        onConfirm={() => create.mutate(true)}
        onCancel={() => setImpact(null)}
      />
    </Card>
  );
}

function HorizonCard() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);
  const setting = useQuery({
    queryKey: ['configuration', session?.tenantId, 'setting', HORIZON_KEY],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/settings/{key}', { params: { path: { key: HORIZON_KEY } } })),
  });
  useEffect(() => {
    if (setting.data) {
      setValue(String(setting.data.value ?? ''));
    }
  }, [setting.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/configuration/settings/{key}', {
          params: { path: { key: HORIZON_KEY } },
          body: { scope: 'Tenant', scopeId: null, value: Number(latinDigits(value)) },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['configuration', session?.tenantId, 'setting', HORIZON_KEY] });
      await queryClient.invalidateQueries({ queryKey: schKeys.all(session?.tenantId) });
    },
  });
  const editable = setting.data?.canEdit ?? false;
  return (
    <Card variant="outlined" data-testid="schedule-horizon">
      <CardContent>
        <Typography variant="subtitle1" component="h2" gutterBottom>
          {t('sch:schedules.horizon')}
        </Typography>
        {setting.error ? (
          <ErrorState error={setting.error} compact />
        ) : (
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <TextField
              size="small"
              label={t('sch:schedules.horizon_days')}
              helperText={t('sch:schedules.horizon_hint')}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={!editable}
              inputProps={{ inputMode: 'numeric', dir: 'ltr', 'data-testid': 'horizon-days' }}
            />
            {editable && (
              <Button variant="outlined" onClick={() => save.mutate()} disabled={save.isPending} data-testid="horizon-save">
                {t('sch:schedules.save')}
              </Button>
            )}
          </Stack>
        )}
        {save.error ? <ErrorState error={save.error} compact /> : null}
        {saved && (
          <Alert severity="success" role="status" sx={{ mt: 1 }}>
            {t('sch:schedules.horizon_saved')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export function SchedulesSettingsPage() {
  const { t } = useTranslation('sch');
  const access = useAccess();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId);
  const [providerId, setProviderId] = useState('');
  const canManage = access.accessMode === 'Full' && access.can('sch.schedule.manage');
  const showHorizon = access.can('sch.policy.manage') && access.can('cfg.settings.view');
  const providers = useMemo(() => options.data?.providers ?? [], [options.data]);
  const rooms = useMemo(() => options.data?.rooms ?? [], [options.data]);

  useEffect(() => {
    if (!providerId && providers.length > 0) {
      setProviderId(providers[0]?.id ?? '');
    }
  }, [providerId, providers]);

  return (
    <Stack data-screen-id={SCR_STAFF_304} spacing={2}>
      <PageHeader titleKey="sch:schedules.title" subtitleKey="sch:schedules.subtitle" />
      {branchId === null ? (
        <BranchRequired />
      ) : options.isPending ? (
        <LoadingState rows={4} />
      ) : options.error ? (
        <ErrorState error={options.error} onRetry={() => void options.refetch()} />
      ) : providers.length === 0 ? (
        <Alert severity="info">{t('sch:schedules.no_providers')}</Alert>
      ) : (
        <>
          <TextField
            select
            label={t('sch:schedules.provider')}
            value={providerId}
            onChange={(event) => setProviderId(event.target.value)}
            sx={{ maxWidth: 360 }}
            inputProps={{ 'data-testid': 'schedule-provider' }}
          >
            {providers.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name}
              </MenuItem>
            ))}
          </TextField>
          {providerId && branchId && options.data && (
            <>
              <TemplateEditor
                key={`${providerId}-template`}
                providerId={providerId}
                branchId={branchId}
                rooms={rooms}
                categories={options.data.categories ?? []}
                canManage={canManage}
                today={options.data.today}
              />
              <ExceptionsCard
                key={`${providerId}-exceptions`}
                providerId={providerId}
                branchId={branchId}
                rooms={rooms}
                canManage={canManage}
                today={options.data.today}
              />
            </>
          )}
        </>
      )}
      {showHorizon && <HorizonCard />}
    </Stack>
  );
}
